import { Button, StandardAlert } from '@cimplico/components';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFirmContext } from '../../providers/FirmProvider';
import { useCreateEntities } from '../../queries/use-create-entity';
import { EntityTemplateCsv } from '../../types/entity';
import { toastNotification } from '../../components/toast/Toast';
import { useGroups } from '../../queries/use-groups';
import { validateData } from './utils';
import plural from '../../utilities/plural';
import Papa from 'papaparse';
import TemplateUploadDropzone from '../../components/file-upload/TemplateUploadDropzone';
import TemplateUploadPreview from '../../components/file-upload/TemplateUploadPreview';

const columns = ['Name *', 'Legal Name *', 'Financial Year End Month *', 'Financial Year End Day *', 'Entity Type *', 'Group'];
const columnString = columns.map((c) => `"${c}"`).join(',');

type ManualBulkEntityFormProps = {
    onLoading: () => void;
    onSubmit: () => void;
    onFileError: (error?: string) => void;
    onCsvRowError: (csvError: { [rowNumber: string]: string[] }) => void;
    onCreateEntityError: (createError: { [name: string]: string[] }) => void;
};

function ManualBulkEntityForm({
    onLoading,
    onSubmit,
    onFileError,
    onCsvRowError,
    onCreateEntityError,
}: ManualBulkEntityFormProps): JSX.Element {
    const { shortId } = useFirmContext();
    const [params] = useSearchParams();
    const returnTo = params.get('returnTo');

    const [file, setFile] = useState<string | undefined>();
    const [data, setData] = useState<EntityTemplateCsv[] | undefined>();
    const [hasError, setHasError] = useState<boolean>(false);

    const useGroupsQuery = useGroups({ page: 1, search: '', sort: '', filter: '' });
    const createEntitiesQuery = useCreateEntities();
    const navigate = useNavigate();

    function handleSubmit(e: React.FormEvent<HTMLFormElement>): void {
        e.preventDefault();

        if (hasError) {
            return;
        }

        if (!data) {
            setHasError(true);
            onFileError('A file must be uploaded.');
            return;
        }

        if (useGroupsQuery.data === undefined) {
            return;
        }

        const result = validateData(data, useGroupsQuery.data.groups);

        if (Object.keys(result[0]).length > 0) {
            setHasError(true);
            onCsvRowError(result[0]);
            return;
        }

        const entityRows = result[1];

        onLoading();
        createEntitiesQuery.mutate(
            {
                entities: entityRows.map((row) => {
                    return { ...row, industry: '' };
                }),
            },
            {
                onSuccess: () => {
                    navigate(`/${shortId}/entities`);
                    toastNotification('success', `${entityRows.length} new ${plural(entityRows.length, 'entity', 'entities')} created!`);
                },
                onError: (res) => {
                    const results = res.response?.data.results;
                    const errorEntities = results.filter((r: { statusCode: number }) => r.statusCode === 400).length;
                    const successEntities = results.filter((r: { statusCode: number }) => r.statusCode === 201).length;

                    const errorResults = results.filter((r: { statusCode: number }) => r.statusCode === 400);

                    const errorMessages: { [name: string]: string[] } = {};
                    for (const res of errorResults) {
                        errorMessages[res.error.original.name] = [res.error.fields.name || res.error.fields.legalName];
                    }

                    onSubmit();
                    setHasError(true);
                    onCreateEntityError(errorMessages);
                    toastNotification('error', 'Create entities error!', `${successEntities} created. ${errorEntities} failed to create.`);
                },
            }
        );
    }

    function upload(files: File[]): void {
        setHasError(false);
        onFileError(undefined);
        setFile(files[0].name);

        if (files[0].type !== 'text/csv') {
            setHasError(true);
            onFileError('Invalid file type. File must be .csv extension.');
            return;
        }

        Papa.parse(files[0], {
            header: true,
            complete: function (results) {
                if (results.data.length === 0) {
                    setHasError(true);
                    onFileError('File must contains at least 2 rows of data.');
                    return;
                }

                if (JSON.stringify(Object.getOwnPropertyNames(results.data[0])) !== JSON.stringify(columns)) {
                    setHasError(true);
                    onFileError('Invalid file. The template should be used.');
                    return;
                }

                setData(results.data as EntityTemplateCsv[]);
            },
        });
    }

    function deleteFile(): void {
        setFile(undefined);
        setData(undefined);
        setHasError(false);
        onFileError(undefined);
        onCsvRowError({});
        onCreateEntityError({});
    }

    return (
        <form className="flex flex-col gap-8" onSubmit={handleSubmit}>
            <StandardAlert
                type="info"
                title="Ensure the following"
                description={[
                    'Entity names and legal names must be unique.',
                    'Group names must match an existing group.',
                    'Entity type must be one of the following: COMPANY, TRUST, PARTNERSHIP, SOLE_TRADER, INDIVIDUAL, UNIT_TRUST, ESTATE, SELF_MANAGED_SUPERANNUATION_FUND, SUPERANNUATION_FUND, GOVERNMENT_ENTITY, CLUB_OR_SOCIETY, NOT_FOR_PROFIT, ACCOUNTING_PRACTICE, CHARITY, LOOK_THROUGH_COMPANY, S_CORPORATION or OTHER.',
                ]}
            >
                <div></div>
            </StandardAlert>
            {/* FILE UPLOAD SECTION */}
            {!file && <TemplateUploadDropzone columns={[columnString]} fileName="entity-template.csv" onUpload={upload} />}

            {/* SHOW UPLOADED FILE NAME */}
            {file && <TemplateUploadPreview fileName={file} onDelete={deleteFile} />}

            {/* FORM CANCEL + SUBMIT */}
            <div className="flex justify-end gap-4">
                <Button type="hollow" label="Cancel" onClick={(): void => navigate(returnTo || '..')} />
                <Button
                    type="primary"
                    label={createEntitiesQuery.isLoading ? 'Creating...' : 'Create Entity'}
                    disabled={createEntitiesQuery.isLoading}
                    isLoading={createEntitiesQuery.isLoading}
                    isSubmit
                />
            </div>
        </form>
    );
}
export default ManualBulkEntityForm;
